import { useState, useEffect, useCallback, useRef } from 'react';
import cacheStorage from '../utils/cacheStorage';

const API_BASE = process.env.REACT_APP_API_URL || 'https://smart-cargo-backend-923182991420.us-central1.run.app';

/**
 * Converts an ISO timestamp to a human-readable relative string.
 */
function timeAgo(isoString) {
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
}

function getAuthToken() {
    return cacheStorage.getItem('access_token');
}

function getAuthHeaders() {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Shared hook for notifications.
 * Returns notifications with a relative `time` field added for display.
 *
 * Falls back gracefully if the backend is unreachable — the UI shows
 * an empty list rather than crashing.
 */
export function useNotifications(userId = 'admin') {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const esRef = useRef(null);

    const enrich = (list) =>
        list.map((n) => ({ ...n, time: timeAgo(n.created_at) }));


    const fetchNotifications = useCallback(async () => {
        try {
            const token = cacheStorage.getItem('access_token');

            const res = await fetch(`${API_BASE}/api/notifications?userId=${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setNotifications(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId]);


    useEffect(() => {
        fetchNotifications();

        const token = getAuthToken();
        const params = new URLSearchParams({ userId });
        if (token) {
            params.set('token', token);
        }

        const es = new EventSource(`${API_BASE}/api/notifications/events?${params.toString()}`);
        esRef.current = es;

        es.onmessage = (e) => {
            try {
                const newNotif = JSON.parse(e.data);
                setNotifications((prev) => [{ ...newNotif, time: timeAgo(newNotif.created_at) }, ...prev]);
            } catch {}
        };

        es.onerror = () => {
            // EventSource reconnects automatically — just log silently
        };

        return () => {
            es.close();
        };
    }, [userId, fetchNotifications]);

    const markAsRead = useCallback(async (id) => {
        try {
            await fetch(`${API_BASE}/api/notifications/${id}/read`, {
                method: 'PUT',
                headers: getAuthHeaders(),
            });
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
            );
        } catch {}
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            await fetch(`${API_BASE}/api/notifications/read?userId=${userId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
            });
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        } catch {}
    }, [userId]);

    const unreadCount = notifications.filter((n) => !n.is_read).length;

    return { notifications, loading, error, unreadCount, markAsRead, markAllAsRead, refetch: fetchNotifications };
}